import React, { useEffect, useMemo } from 'react'
import TabPanel from '../molecules/TabPanel';
import { makeStyles } from '@material-ui/core';
import Player from '../types/Player';
import PlayerProfileTeamTypeSwitcher from '../molecules/PlayerProfileTeamTypeSwitcher';
import { TEAM_TYPE } from '../templates/UpdatedPlayerPageTemplate';

interface PlayerProfileStickyHeaderProps {
  player: Player | undefined,
  selectedTeamType: string,
  selectedTab: string | null,
  tabsAvailability?: {
    production?: boolean,
    valuation?: boolean,
    inGame?: boolean,
    ppi?: boolean,
    hsRatings?: boolean,
    comps?: boolean,
    proCombine?: boolean,
  },
  setSelectedTab: React.Dispatch<React.SetStateAction<string | null>>,
  setSelectedTeamType: React.Dispatch<React.SetStateAction<string>>,
}

type AvailabilityKey = 'proCombine' | 'production' | 'valuation' | 'inGame' | 'ppi' | 'hsRatings' | 'comps';

const TABS_ENUM = {
  OVERVIEW: "Overview",
  HS_RATINGS: "HS Ratings",
  PRODUCTION: "Production",
  VALUATION: "Valuation",
  PPI: "PPI",
  PRO_COMBINE: "Pro Combine",
  IN_GAME: "In-Game",
  COMPS: "Comps"
};
const TABS_VALUES_ENUM = {
  OVERVIEW: "overview",
  HS_RATINGS: "hsRatings",
  PRODUCTION: "production",
  VALUATION: "valuation",
  PPI: "ppi",
  PRO_COMBINE: "proCombine",
  IN_GAME: "collegeInGame",
  COMPS: "comps"
};

type TabItem = {
  label: string;
  value: string;
  availabilityKey?: AvailabilityKey;
  disabled?: boolean;
};

const tabsList: TabItem[] = [
  {label: TABS_ENUM.OVERVIEW, value: TABS_VALUES_ENUM.OVERVIEW},
  {label: TABS_ENUM.HS_RATINGS, value: TABS_VALUES_ENUM.HS_RATINGS, availabilityKey: 'hsRatings'},
  {label: TABS_ENUM.PRODUCTION, value: TABS_VALUES_ENUM.PRODUCTION, availabilityKey: 'production'},
  {label: TABS_ENUM.VALUATION, value: TABS_VALUES_ENUM.VALUATION, availabilityKey: 'valuation'},
  {label: TABS_ENUM.COMPS, value: TABS_VALUES_ENUM.COMPS, availabilityKey: 'comps'}
];

function getTabsByTeamType(selectedTeamType: string): TabItem[] {
  switch (selectedTeamType) {
    case TEAM_TYPE.COLLEGE:
      return [
        {label: TABS_ENUM.OVERVIEW, value: TABS_VALUES_ENUM.OVERVIEW},
        {label: TABS_ENUM.PRODUCTION, value: TABS_VALUES_ENUM.PRODUCTION, availabilityKey: 'production'},
        {label: TABS_ENUM.PPI, value: TABS_VALUES_ENUM.PPI, availabilityKey: 'ppi'},
        {label: TABS_ENUM.IN_GAME, value: TABS_VALUES_ENUM.IN_GAME, availabilityKey: 'inGame'},
        {label: TABS_ENUM.VALUATION, value: TABS_VALUES_ENUM.VALUATION, availabilityKey: 'valuation'},
        {label: TABS_ENUM.HS_RATINGS, value: TABS_VALUES_ENUM.HS_RATINGS, availabilityKey: 'hsRatings'},
        {label: TABS_ENUM.COMPS, value: TABS_VALUES_ENUM.COMPS, availabilityKey: 'comps'},
      ];
    case TEAM_TYPE.NFL:
      return [
        {label: TABS_ENUM.OVERVIEW, value: TABS_VALUES_ENUM.OVERVIEW},
        {label: TABS_ENUM.PRODUCTION, value: TABS_VALUES_ENUM.PRODUCTION, availabilityKey: 'production'},
        {label: TABS_ENUM.PRO_COMBINE, value: TABS_VALUES_ENUM.PRO_COMBINE, availabilityKey: 'proCombine'},
        {label: TABS_ENUM.PPI, value: TABS_VALUES_ENUM.PPI, availabilityKey: 'ppi'},
        {label: TABS_ENUM.VALUATION, value: TABS_VALUES_ENUM.VALUATION, availabilityKey: 'valuation'},
        {label: TABS_ENUM.HS_RATINGS, value: TABS_VALUES_ENUM.HS_RATINGS, availabilityKey: 'hsRatings'},
        {label: TABS_ENUM.COMPS, value: TABS_VALUES_ENUM.COMPS, availabilityKey: 'comps'},
      ];
    case TEAM_TYPE.HIGH_SCHOOL:
    default:
      return [...tabsList];
  }
}

const useStyles = makeStyles(theme => ({
  viewSectionWrap: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    gap: theme.spacing(3),

    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column-reverse',
      alignItems: 'stretch',
      gap: theme.spacing(2),
    },
  },
}), { name: PlayerProfileStickyHeader.name });

export default function PlayerProfileStickyHeader(props: PlayerProfileStickyHeaderProps) {
  const {
    player,
    selectedTeamType,
    selectedTab,
    setSelectedTab,
    setSelectedTeamType,
    tabsAvailability,
  } = props;
  const classes = useStyles();

  const tabs = useMemo(() => {
    const newTabs = getTabsByTeamType(selectedTeamType);

    return newTabs.map(tab => {
      if (!tab.availabilityKey) {
        return tab;
      }

      return {
        ...tab,
        disabled: tabsAvailability?.[tab.availabilityKey] === false,
      };
    });
  }, [selectedTeamType, tabsAvailability]);

  useEffect(() => {
    if (!selectedTab) {
      return;
    }

    const selectedTabItem = tabs.find(tab => tab.value === selectedTab);
    if (selectedTabItem && !selectedTabItem.disabled) {
      return;
    }

    const nextTab = tabs.find(tab => !tab.disabled)?.value || TABS_VALUES_ENUM.OVERVIEW;
    if (selectedTab !== nextTab) {
      setSelectedTab(nextTab);
    }
  }, [selectedTab, setSelectedTab, tabs]);

  return (
    <div className={classes.viewSectionWrap}>
      <TabPanel 
        tabsList={tabs}
        selectedTab={selectedTab} 
        setSelectedTab={setSelectedTab}
        player={player} 
      />
      <PlayerProfileTeamTypeSwitcher
        player={player}
        selectedTeamType={selectedTeamType}
        setSelectedTeamType={setSelectedTeamType}
      />
    </div>
  )
}
